import { HotkeyDisplay } from '@app/core/hotkeys/HotkeyDisplay';
import { useFragmentCreationStore } from '@app/modules/fragments/stores/fragment-creation.store';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  ButtonGroup,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { MouseEvent, ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  createAndGoShortMessage,
  createAndOpenMessage,
  createOnlyMessage,
} from './create-fragment-submit-messages';
import {
  CREATE_FRAGMENT_SPLIT_SUBMIT_TOUR_ELEMENT_ID,
  useCreateFragmentSplitSubmitTour,
} from './useCreateFragmentSplitSubmitTour';

/** Width (px) of the caret button that opens the alternate create action. */
const CARET_BUTTON_WIDTH_PX = 24;

type CreateFragmentSplitSubmitButtonProps = {
  showTick: boolean;
  saveDisabled: boolean;
  isSubmitLoading: boolean;
  showSubmitHotkey: boolean;
  submitHotkey: string | null | undefined;
  submitDisabledTooltip?: ReactNode;
};

/**
 * Split submit for fragment creation: primary runs the remembered mode
 * (create vs create-and-go). Choosing the other option from the caret menu
 * only updates the primary button; the user clicks it again to submit.
 */
export function CreateFragmentSplitSubmitButton({
  showTick,
  saveDisabled,
  isSubmitLoading,
  showSubmitHotkey,
  submitHotkey,
  submitDisabledTooltip,
}: CreateFragmentSplitSubmitButtonProps) {
  const { $t } = useIntl();
  const { closeTour } = useCreateFragmentSplitSubmitTour();
  const navigateAfterCreate = useFragmentCreationStore((s) => s.navigateAfterCreate);
  const setNavigateAfterCreate = useFragmentCreationStore((s) => s.setNavigateAfterCreate);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Dismiss the tour if the create drawer unmounts while it is still open.
  useEffect(() => closeTour, [closeTour]);

  const primaryLabel = navigateAfterCreate ? $t(createAndGoShortMessage) : $t(createOnlyMessage);
  const alternateNavigate = !navigateAfterCreate;
  const alternateLabel = alternateNavigate ? $t(createAndOpenMessage) : $t(createOnlyMessage);
  const alternateIcon = alternateNavigate ? (
    <OpenInNewIcon fontSize="small" />
  ) : (
    <SaveIcon fontSize="small" />
  );

  const submitEndIcon =
    showSubmitHotkey && submitHotkey ? (
      <HotkeyDisplay
        hotkey={submitHotkey}
        sx={{ ml: 0.5, fontSize: '0.8rem !important', opacity: 0.7 }}
      />
    ) : undefined;

  /** Opens the alternate-action menu from the caret. */
  function handleOpenMenu(event: MouseEvent<HTMLButtonElement>) {
    closeTour();
    setMenuAnchor(event.currentTarget);
  }

  function handleCloseMenu() {
    setMenuAnchor(null);
  }

  /**
   * Remembers the alternate mode and updates the primary button label.
   * Does not submit — the user must click the primary button to create.
   */
  function handleSelectAlternate() {
    setNavigateAfterCreate(alternateNavigate);
    handleCloseMenu();
  }

  const submitLabel = showTick ? <CheckIcon /> : primaryLabel;

  const buttonGroup = (
    <ButtonGroup variant="contained" color="primary" disabled={saveDisabled} sx={{ minWidth: 130 }}>
      <Button
        type="submit"
        loading={isSubmitLoading}
        disabled={saveDisabled}
        endIcon={submitEndIcon}
        sx={{ flexGrow: 1 }}
      >
        {submitLabel}
      </Button>
      <Button
        id={CREATE_FRAGMENT_SPLIT_SUBMIT_TOUR_ELEMENT_ID}
        type="button"
        size="small"
        disabled={saveDisabled || isSubmitLoading || showTick}
        onClick={handleOpenMenu}
        sx={{
          px: 0,
          minWidth: CARET_BUTTON_WIDTH_PX,
          width: CARET_BUTTON_WIDTH_PX,
          // ButtonGroup sets a wider min-width on grouped buttons; keep the caret slim.
          '&.MuiButtonGroup-grouped': {
            minWidth: CARET_BUTTON_WIDTH_PX,
          },
        }}
      >
        <ArrowDropDownIcon sx={{ fontSize: 20 }} />
      </Button>
    </ButtonGroup>
  );

  return (
    <>
      {saveDisabled && submitDisabledTooltip ? (
        <Tooltip title={submitDisabledTooltip}>
          <span>{buttonGroup}</span>
        </Tooltip>
      ) : (
        buttonGroup
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSelectAlternate}>
          <ListItemIcon>{alternateIcon}</ListItemIcon>
          <ListItemText>{alternateLabel}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
